import { useEffect } from "react";
import { TextField } from "@mui/material";

import MyAlert from "~/mui/MyAlert";
import ExternalLink from "~/components/ExternalLink";
import BetterTariffSpecificationContent, { TVariant } from "~/components/BetterTariffSpecificationContent";
import { LICENCE_AGGR } from "~/const";
import { useTrans } from "~/hooks/useTrans";
import { checkForErrorsState, fillUrl } from '~/utils/utils';
import { IUrlParams } from "~/interfaces";
import { apiSignNewConsent, apiGetProjectAdvancedBillingConsent } from "~/providers/api";
import MyDialog from "~/mui/MyDialog";
import { myUseState } from "~/hooks/myUseState";
import { notificationStore } from "~/stores/notification";
import { useLang } from "~/hooks/useLang";
import MyCheckboxAndLink from "~/mui/MyCheckboxAndLink";
import MyCheckbox from "~/mui/MyCheckbox";
import TextFieldLen from "~/mui/TextFieldLen";

import "./style.less";

const RANGES = {
	/* eslint-disable-next-line */
	desc: [10, 1000],
};

interface IState {
	showSpecification: boolean;
	variant: TVariant;
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
}

interface IStateForm {
	address: string;
	desc: string;
	isOption1: boolean;
	isOption2: boolean;
	isOption3: boolean;
	isOption4: boolean;
	isOption5: boolean;
}

type TStateFormErrors = {[key in keyof Pick<IStateForm, "address" | "desc">]: string; };

interface IBetterTariffRequestModal {
	params: IUrlParams;
	onClose?: () => void;
	onSave?: () => void;
	onChange?: (isChanged: boolean) => void;
}

export default function BetterTariffRequestModal({
	params = null,
	onClose = () => {},
	onSave = () => {},
	onChange = () => {},
}: IBetterTariffRequestModal) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { setNotification } = notificationStore(notificationState => ({
		setNotification: notificationState.setNotification,
	}));
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
		showSpecification: false,
		variant: "option1",
	});
	const { state: stateForm, updateState: updateStateForm } = myUseState<IStateForm>({
		address: "",
		desc: "",
		isOption1: false,
		isOption2: false,
		isOption3: false,
		isOption4: false,
		isOption5: false,
	});
	const { state: stateFormError, setState: setStateFormError } = myUseState<TStateFormErrors>({
		address: "",
		desc: "",
	});

	function getWasChanged(newState: Partial<IStateForm>): boolean {
		const newStateObj = {
			...stateForm,
			...newState,
		};

		return newStateObj.address !== ""
			|| newStateObj.desc !== ""
			|| newStateObj.isOption1
			|| newStateObj.isOption2
			|| newStateObj.isOption3
			|| newStateObj.isOption4;
	}

	function updateStateFormWithChange(newState: Partial<IStateForm>) {
		const wasChanged = getWasChanged(newState);

		updateStateForm(newState);
		updateState({
			wasChanged,
		});
		onChange(wasChanged);
	}

	function getDisableSave() {
		return !state.wasChanged || !stateForm.isOption1 || !stateForm.isOption2 || !stateForm.isOption3 || !stateForm.isOption4;
	}

	async function dialogOnSave() {
		// validTill - nemuze byt prazdne
		const formData: IStateForm = {
			...stateForm,
			address: fillUrl(stateForm.address),
		};
		const errorData = checkForErrorsState<IStateForm, TStateFormErrors>(formData, {
			address: { check: "project-url", msg: trans("betterTariffRequestModal.error.address") },
			desc: { check: "string-gt9", msg: trans("betterTariffRequestModal.error.desc") },
		});

		setStateFormError(errorData.errorsState);

		// jsou tam chyby
		if (errorData.errorsCount > 0) {
			return;
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const confirmData = await apiSignNewConsent(params.projectId, {
			projectAddress: formData.address,
			projectDescription: formData.desc,
		}, lang);

		if (confirmData.noErrors) {
			setNotification(trans("betterTariffRequestModal.afterSave"));
			onSave();
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: confirmData.errors.join(", "),
			});
		}
	}

	async function loadData() {
		// aktualizace dat z predesleho prohlaseni
		const advancedBillingConsentData = await apiGetProjectAdvancedBillingConsent("client", params.projectId, lang);

		if (advancedBillingConsentData.noErrors && advancedBillingConsentData.data.length > 0) {
			const firstItem = advancedBillingConsentData.data[0];
			const validTill = new Date(firstItem.validTill);
			const isValid = validTill.getTime() > Date.now();

			isValid && updateStateForm({
				address: firstItem.projectAddress,
				desc: firstItem.projectDescription,
			});
		}
	}

	useEffect(() => {
		loadData();
	}, []);

	return <MyDialog className="betterTariffRequestModal" title={trans("betterTariffRequestModal.title")} onClose={onClose} onSave={dialogOnSave} dialogLoading={state.dialogLoading} errorMsg={state.errorMsg}
		cancelCaption={ trans("cancel") } saveCaption={ trans("betterTariffRequestModal.save")} disableSave={getDisableSave()} confirmClose={state.wasChanged}
	>
		<p className="margin-bottom-large" dangerouslySetInnerHTML={{ __html: trans("betterTariffRequestModal.info", { br: "<br /><br />" }) }} />
		<TextField
			label={trans("betterTariffRequestModal.address")}
			value={stateForm.address}
			style={{ width: "100%" }}
			onChange={event => updateStateFormWithChange({ address: event.target.value })}
			color="success"
			autoComplete="off"
			sx={{ marginBottom: "var(--margin-large)" }}
			error={stateFormError.address !== ""}
			helperText={stateFormError.address || trans("betterTariffRequestModal.addressInfo")}
			size="small"
		/>
		<TextFieldLen
			label={trans("betterTariffRequestModal.desc")}
			value={stateForm.desc}
			style={{ width: "100%" }}
			onChange={event => updateStateFormWithChange({ desc: event.target.value })}
			color="success"
			autoComplete="off"
			sx={{ marginBottom: "var(--margin-large)" }}
			error={stateFormError.desc !== ""}
			helperText={stateFormError.desc || trans("betterTariffRequestModal.descInfo")}
			size="small"
			rows={3}
			multiline={true}
			lenRange={RANGES.desc}
		/>
		<MyCheckboxAndLink checked={stateForm.isOption1} onChange={checked => updateStateFormWithChange({ isOption1: checked })} label={trans("betterTariffRequestModal.option1")}
			linkLabel={trans("betterTariffRequestModal.specification")} onLinkClick={() => updateState({ showSpecification: true, variant: "option1" })}
		/>
		<MyCheckboxAndLink className="margin-top-small" checked={stateForm.isOption2} onChange={checked => updateStateFormWithChange({ isOption2: checked })}
			label={trans("betterTariffRequestModal.option2")} linkLabel={trans("betterTariffRequestModal.specification")} onLinkClick={() => updateState({ showSpecification: true, variant: "option2" })}
		/>
		<MyCheckboxAndLink className="margin-top-small" checked={stateForm.isOption3} onChange={checked => updateStateFormWithChange({ isOption3: checked })}
			label={trans("betterTariffRequestModal.option3")} linkLabel={trans("betterTariffRequestModal.specification")} onLinkClick={() => updateState({ showSpecification: true, variant: "option3" })}
		/>
		<MyCheckboxAndLink className="betterTariffRequestModal__thirdSwitch" checked={stateForm.isOption5} onChange={checked => updateStateFormWithChange({ isOption5: checked })}
			label={trans("betterTariffRequestModal.option5")} linkLabel={trans("betterTariffRequestModal.specification")} onLinkClick={() => updateState({ showSpecification: true, variant: "option5" })}
		/>
		<MyCheckbox checked={stateForm.isOption4} onChange={checked => updateStateFormWithChange({ isOption4: checked })} label={<>
			{ trans("betterTariffRequestModal.option4") }
			&nbsp;
			<ExternalLink to={LICENCE_AGGR[lang]} sx={{ fontSize: "var(--default-font-size)" }}>
				{ trans("betterTariffRequestModal.option4.link") }
			</ExternalLink>
		</>} />
		{ state.showSpecification && <MyAlert onClose={() => updateState({ showSpecification: false })} title={trans("betterTariffRequestModal.specification")} cancelCaption={trans("close")}>
			<BetterTariffSpecificationContent variant={state.variant} lang={lang} />
		</MyAlert> }
	</MyDialog>;
}
